import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Plus, TrendingUp, TrendingDown, Minus, Edit, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import UserAvatar from '../components/UserAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSScorecard = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updateValueDialogOpen, setUpdateValueDialogOpen] = useState(false);
  const [trendDialogOpen, setTrendDialogOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [users, setUsers] = useState([]);

  // Form state
  const [metricForm, setMetricForm] = useState({
    name: '',
    description: '',
    owner_id: '',
    department: '',
    measurement_type: 'number',
    measurement_frequency: 'weekly',
    goal: '',
    thresholds: {
      green_min: '',
      yellow_min: '',
      red_max: ''
    },
    calculation_formula: ''
  });

  // Update value form state
  const [valueForm, setValueForm] = useState({
    value: '',
    notes: ''
  });

  useEffect(() => {
    fetchMetrics();
    fetchUsers();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/eos/scorecard/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load scorecard metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateMetric = async () => {
    try {
      // Validate
      if (!metricForm.name || !metricForm.owner_id || !metricForm.goal) {
        toast.error('Please fill in all required fields');
        return;
      }

      const payload = {
        ...metricForm,
        goal: parseFloat(metricForm.goal),
        thresholds: {
          green_min: parseFloat(metricForm.thresholds.green_min),
          yellow_min: parseFloat(metricForm.thresholds.yellow_min),
          red_max: parseFloat(metricForm.thresholds.red_max)
        }
      };

      await axios.post(`${API}/eos/scorecard/metrics`, payload);
      toast.success('Metric created successfully');
      setCreateDialogOpen(false);
      resetMetricForm();
      fetchMetrics();
    } catch (error) {
      console.error('Error creating metric:', error);
      toast.error(error.response?.data?.detail || 'Failed to create metric');
    }
  };

  const handleUpdateMetric = async () => {
    try {
      if (!selectedMetric) return;

      const payload = {
        name: metricForm.name,
        description: metricForm.description,
        owner_id: metricForm.owner_id,
        goal: parseFloat(metricForm.goal),
        thresholds: {
          green_min: parseFloat(metricForm.thresholds.green_min),
          yellow_min: parseFloat(metricForm.thresholds.yellow_min),
          red_max: parseFloat(metricForm.thresholds.red_max)
        }
      };

      await axios.put(`${API}/eos/scorecard/metrics/${selectedMetric.id}`, payload);
      toast.success('Metric updated successfully');
      setEditDialogOpen(false);
      setSelectedMetric(null);
      resetMetricForm();
      fetchMetrics();
    } catch (error) {
      console.error('Error updating metric:', error);
      toast.error('Failed to update metric');
    }
  };

  const handleUpdateValue = async () => {
    try {
      if (!selectedMetric || !valueForm.value) {
        toast.error('Please enter a value');
        return;
      }

      // Get current week start date (Monday)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      monday.setHours(0, 0, 0, 0);

      await axios.post(`${API}/eos/scorecard/metrics/${selectedMetric.id}/update-value`, null, {
        params: {
          week_start_date: monday.toISOString(),
          value: parseFloat(valueForm.value),
          notes: valueForm.notes
        }
      });

      toast.success('Value updated successfully');
      setUpdateValueDialogOpen(false);
      setSelectedMetric(null);
      setValueForm({ value: '', notes: '' });
      fetchMetrics();
    } catch (error) {
      console.error('Error updating value:', error);
      toast.error('Failed to update value');
    }
  };

  const openEditDialog = (metric) => {
    setSelectedMetric(metric);
    setMetricForm({
      name: metric.name,
      description: metric.description,
      owner_id: metric.owner_id,
      department: metric.department,
      measurement_type: metric.measurement_type,
      measurement_frequency: metric.measurement_frequency,
      goal: metric.goal.toString(),
      thresholds: {
        green_min: metric.thresholds.green_min.toString(),
        yellow_min: metric.thresholds.yellow_min.toString(),
        red_max: metric.thresholds.red_max.toString()
      },
      calculation_formula: metric.calculation_formula
    });
    setEditDialogOpen(true);
  };

  const openUpdateValueDialog = (metric) => {
    setSelectedMetric(metric);
    // Pre-fill with last value if exists
    const lastValue = metric.weekly_data?.[metric.weekly_data.length - 1]?.value;
    setValueForm({
      value: lastValue ? lastValue.toString() : '',
      notes: ''
    });
    setUpdateValueDialogOpen(true);
  };

  const openTrendDialog = (metric) => {
    setSelectedMetric(metric);
    setTrendDialogOpen(true);
  };

  const resetMetricForm = () => {
    setMetricForm({
      name: '',
      description: '',
      owner_id: '',
      department: '',
      measurement_type: 'number',
      measurement_frequency: 'weekly',
      goal: '',
      thresholds: {
        green_min: '',
        yellow_min: '',
        red_max: ''
      },
      calculation_formula: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getCurrentWeekValue = (metric) => {
    if (!metric.weekly_data || metric.weekly_data.length === 0) {
      return { value: '-', status: 'not_entered', trend: 'flat' };
    }
    const lastEntry = metric.weekly_data[metric.weekly_data.length - 1];
    return {
      value: lastEntry.value !== null ? lastEntry.value : '-',
      status: lastEntry.status,
      trend: lastEntry.trend
    };
  };

  const formatChartData = (metric) => {
    if (!metric.weekly_data || metric.weekly_data.length === 0) return [];
    
    return metric.weekly_data.slice(-13).map((entry, index) => ({
      week: `W${index + 1}`,
      value: entry.value,
      goal: metric.goal
    }));
  };

  // Calculate overall scorecard status
  const calculateOverallStatus = () => {
    if (metrics.length === 0) return { green: 0, yellow: 0, red: 0, notEntered: 0, total: 0 };
    
    const counts = { green: 0, yellow: 0, red: 0, notEntered: 0, total: metrics.length };
    
    metrics.forEach(metric => {
      const current = getCurrentWeekValue(metric);
      if (current.status === 'not_entered') counts.notEntered++;
      else if (current.status === 'green') counts.green++;
      else if (current.status === 'yellow') counts.yellow++;
      else if (current.status === 'red') counts.red++;
    });
    
    return counts;
  };

  const overallStatus = calculateOverallStatus();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Scorecard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-3xl font-bold">Weekly Scorecard</h1>
              <p className="text-sm text-muted-foreground">
                Track your key metrics every week
              </p>
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Metric
          </Button>
        </div>

        {/* Overall Status */}
        <Card>
          <CardHeader>
            <CardTitle>Overall Status</CardTitle>
            <CardDescription>Week of {new Date().toLocaleDateString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold">{overallStatus.total}</div>
                <div className="text-sm text-muted-foreground">Total Metrics</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{overallStatus.green}</div>
                <div className="text-sm text-muted-foreground">🟢 Green</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{overallStatus.yellow}</div>
                <div className="text-sm text-muted-foreground">🟡 Yellow</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{overallStatus.red}</div>
                <div className="text-sm text-muted-foreground">🔴 Red</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-600">{overallStatus.notEntered}</div>
                <div className="text-sm text-muted-foreground">Not Entered</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                {overallStatus.green > 0 && (
                  <div 
                    className="bg-green-500" 
                    style={{ width: `${(overallStatus.green / overallStatus.total) * 100}%` }}
                  ></div>
                )}
                {overallStatus.yellow > 0 && (
                  <div 
                    className="bg-yellow-500" 
                    style={{ width: `${(overallStatus.yellow / overallStatus.total) * 100}%` }}
                  ></div>
                )}
                {overallStatus.red > 0 && (
                  <div 
                    className="bg-red-500" 
                    style={{ width: `${(overallStatus.red / overallStatus.total) * 100}%` }}
                  ></div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics Table */}
        <Card>
          <CardHeader>
            <CardTitle>Metrics</CardTitle>
            <CardDescription>Click any metric to view 13-week trend</CardDescription>
          </CardHeader>
          <CardContent>
            {metrics.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No metrics yet. Add your first metric to get started!</p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Metric
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Metric Name</th>
                      <th className="text-left p-3 font-semibold">Owner</th>
                      <th className="text-center p-3 font-semibold">Goal</th>
                      <th className="text-center p-3 font-semibold">Actual</th>
                      <th className="text-center p-3 font-semibold">Status</th>
                      <th className="text-center p-3 font-semibold">Trend</th>
                      <th className="text-right p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.map(metric => {
                      const current = getCurrentWeekValue(metric);
                      return (
                        <tr 
                          key={metric.id} 
                          className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => openTrendDialog(metric)}
                        >
                          <td className="p-3">
                            <div>
                              <div className="font-medium">{metric.name}</div>
                              <div className="text-xs text-muted-foreground">{metric.department}</div>
                            </div>
                          </td>
                          <td className="p-3">
                            {metric.owner_display || metric.owner_details ? (
                              <UserAvatar 
                                user={metric.owner_display || metric.owner_details} 
                                size="sm" 
                                showName 
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground">N/A</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline">{metric.goal}</Badge>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-semibold">{current.value}</span>
                          </td>
                          <td className="p-3 text-center">
                            <div className={`w-3 h-3 rounded-full mx-auto ${getStatusColor(current.status)}`}></div>
                          </td>
                          <td className="p-3 text-center">
                            {getTrendIcon(current.trend)}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openUpdateValueDialog(metric)}
                              >
                                Update
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => openEditDialog(metric)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Metric Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Metric</DialogTitle>
              <DialogDescription>Add a new metric to your weekly scorecard</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Metric Name *</Label>
                <Input
                  placeholder="e.g., Weekly Revenue"
                  value={metricForm.name}
                  onChange={(e) => setMetricForm({ ...metricForm, name: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Brief description of the metric"
                  value={metricForm.description}
                  onChange={(e) => setMetricForm({ ...metricForm, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Owner *</Label>
                  <Select 
                    value={metricForm.owner_id}
                    onValueChange={(value) => setMetricForm({ ...metricForm, owner_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Department *</Label>
                  <Select 
                    value={metricForm.department}
                    onValueChange={(value) => setMetricForm({ ...metricForm, department: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Measurement Type</Label>
                  <Select 
                    value={metricForm.measurement_type}
                    onValueChange={(value) => setMetricForm({ ...metricForm, measurement_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="ratio">Ratio</SelectItem>
                      <SelectItem value="yes_no">Yes/No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Frequency</Label>
                  <Select 
                    value={metricForm.measurement_frequency}
                    onValueChange={(value) => setMetricForm({ ...metricForm, measurement_frequency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Goal (Target Value) *</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50000"
                  value={metricForm.goal}
                  onChange={(e) => setMetricForm({ ...metricForm, goal: e.target.value })}
                />
              </div>

              <div className="border rounded-lg p-4 space-y-4">
                <div className="font-semibold">Status Thresholds</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>🟢 Green Min</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50000"
                      value={metricForm.thresholds.green_min}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, green_min: e.target.value }
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">≥ this value</p>
                  </div>
                  <div>
                    <Label>🟡 Yellow Min</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 40000"
                      value={metricForm.thresholds.yellow_min}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, yellow_min: e.target.value }
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">≥ this value</p>
                  </div>
                  <div>
                    <Label>🔴 Red Max</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 40000"
                      value={metricForm.thresholds.red_max}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, red_max: e.target.value }
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">&lt; this value</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Calculation Formula (Optional)</Label>
                <Input
                  placeholder="e.g., Total Sales / Number of Leads"
                  value={metricForm.calculation_formula}
                  onChange={(e) => setMetricForm({ ...metricForm, calculation_formula: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetMetricForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateMetric}>Create Metric</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Metric Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Metric</DialogTitle>
              <DialogDescription>Update metric details</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Metric Name *</Label>
                <Input
                  value={metricForm.name}
                  onChange={(e) => setMetricForm({ ...metricForm, name: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={metricForm.description}
                  onChange={(e) => setMetricForm({ ...metricForm, description: e.target.value })}
                />
              </div>

              <div>
                <Label>Owner *</Label>
                <Select 
                  value={metricForm.owner_id}
                  onValueChange={(value) => setMetricForm({ ...metricForm, owner_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Goal *</Label>
                <Input
                  type="number"
                  value={metricForm.goal}
                  onChange={(e) => setMetricForm({ ...metricForm, goal: e.target.value })}
                />
              </div>

              <div className="border rounded-lg p-4 space-y-4">
                <div className="font-semibold">Status Thresholds</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>🟢 Green Min</Label>
                    <Input
                      type="number"
                      value={metricForm.thresholds.green_min}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, green_min: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label>🟡 Yellow Min</Label>
                    <Input
                      type="number"
                      value={metricForm.thresholds.yellow_min}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, yellow_min: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label>🔴 Red Max</Label>
                    <Input
                      type="number"
                      value={metricForm.thresholds.red_max}
                      onChange={(e) => setMetricForm({ 
                        ...metricForm, 
                        thresholds: { ...metricForm.thresholds, red_max: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setEditDialogOpen(false);
                setSelectedMetric(null);
                resetMetricForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateMetric}>Update Metric</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Value Dialog */}
        <Dialog open={updateValueDialogOpen} onOpenChange={setUpdateValueDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Metric Value</DialogTitle>
              <DialogDescription>
                {selectedMetric?.name} - Week of {new Date().toLocaleDateString()}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Value *</Label>
                <Input
                  type="number"
                  placeholder="Enter this week's value"
                  value={valueForm.value}
                  onChange={(e) => setValueForm({ ...valueForm, value: e.target.value })}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Goal: {selectedMetric?.goal}
                </p>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="Any comments or context"
                  value={valueForm.notes}
                  onChange={(e) => setValueForm({ ...valueForm, notes: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setUpdateValueDialogOpen(false);
                setSelectedMetric(null);
                setValueForm({ value: '', notes: '' });
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateValue}>Update Value</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Trend Chart Dialog */}
        <Dialog open={trendDialogOpen} onOpenChange={setTrendDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{selectedMetric?.name}</DialogTitle>
              <DialogDescription>13-week trend</DialogDescription>
            </DialogHeader>

            {selectedMetric && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Goal</div>
                    <div className="text-2xl font-bold">{selectedMetric.goal}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Current</div>
                    <div className="text-2xl font-bold">
                      {getCurrentWeekValue(selectedMetric).value}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Status</div>
                    <div className="text-2xl font-bold">
                      {getCurrentWeekValue(selectedMetric).status === 'green' && '🟢 On Target'}
                      {getCurrentWeekValue(selectedMetric).status === 'yellow' && '🟡 Warning'}
                      {getCurrentWeekValue(selectedMetric).status === 'red' && '🔴 Below Target'}
                      {getCurrentWeekValue(selectedMetric).status === 'not_entered' && '⚪ Not Entered'}
                    </div>
                  </div>
                </div>

                {formatChartData(selectedMetric).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={formatChartData(selectedMetric)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <ReferenceLine y={selectedMetric.goal} stroke="#22c55e" strokeDasharray="3 3" label="Goal" />
                      <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No data yet. Update values weekly to see trends.
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => {
                setTrendDialogOpen(false);
                setSelectedMetric(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSScorecard;
